import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Card,
  Filters,
  Icon,
  linkTextClasses,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  rowLinkClasses,
  Table,
  type FilterFieldSlot,
} from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';
import { knownCodes, MirrorStamp, StatusBadge } from './common.js';

/**
 * O cadastro de veículos da praça — `erp-37`.
 *
 * ## Por que a busca é um campo só
 *
 * Quem atende tem **um** dado na mão: o que a pessoa do outro lado do telefone
 * soube dizer. Quatro campos — placa, chassi, renavam, documento — obrigariam a
 * escolher em qual digitar antes de saber qual é, e a escolha errada devolve
 * "não encontrado" sobre um cadastro que existe.
 *
 * ## O que esta tela NÃO faz, e diz que não faz
 *
 * Não edita. O espelho é o que o ERP diz, e mudar o que o ERP diz é a porta de
 * escrita — que hoje aponta para o endereço errado na alteração de situação
 * (`docs/modulos/erp/plano-pendencias.md`). Um botão aqui seria promessa que a
 * camada de baixo não cumpre.
 */
export function VehiclesScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/veiculos' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const situacao = search.situacao ?? '';
  /* o plano que `/erp/planos` abre, no endereço para o link ser copiável */
  const plano = search.plano;
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: ['erp', 'veiculos', tenantId, term, situacao, plano, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchVehicles({
        body: {
          term: term.length >= 2 ? term : undefined,
          situacao: situacao ? [situacao] : undefined,
          plan: plano,
          page,
          perPage,
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'os veículos'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  /* os códigos que a praça conhece, para o filtro não ser um campo de digitar */
  const classifications = useQuery({
    queryKey: ['erp', 'classificacoes', tenantId],
    queryFn: async () => {
      const r = await api.erp.listClassifications({ query: {} });
      return r.status === 200 ? r.body : null;
    },
  });

  const codes = knownCodes(classifications.data, 'veiculo');
  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'situacao',
      label: 'Situação',
      placeholder: 'Todas',
      /* o NOME da situação, como o ERP a chama; o código só quando o nome falta */
      options: codes.map((c) => ({
        value: c.code,
        label: c.descricao ?? `Situação ${c.code}`,
      })),
    },
  ];

  return (
    <Page measure="cheia">
      <PageHeader title="Veículos">
        O cadastro de {context?.currentTenant.nome}, como o ERP o entrega.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {plano && (
        <Alert tone="informacao">
          Só os veículos do plano de código {plano} no ERP.{' '}
          <button
            type="button"
            className={linkTextClasses()}
            onClick={() => list.setFilters({ plano: undefined })}
          >
            Ver todos
          </button>
        </Alert>
      )}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} veículo(s)`}
          descricao={<MirrorStamp at={result?.syncedAt ?? null} />}
          bleed
        >
          <Table
            paginate={false}
            /*
             * A busca é da barra e vale ao digitar; a situação é do painel e só
             * vale quando alguém confirma. Ver o cabeçalho de `Filters`.
             */
            search={{
              label: 'Pesquisar veículos',
              placeholder: 'Placa, Código, Chassi, Renavam, CPF/CNPJ ou Nome do Dono',
              value: text,
              onChange: setText,
            }}
            toolbar={
              <Filters
                values={{ situacao }}
                fields={fields}
                onApply={(values) =>
                  list.setFilters({
                    situacao: String(values.situacao ?? '') || undefined,
                  })
                }
              />
            }
            label="Veículos do cadastro"
            items={result?.estado === 'lida' ? result.items : []}
            loading={query.isLoading}
            rowKey={(v) => v.code}
            empty={
              term || situacao || plano
                ? 'Nenhum veículo com esses filtros.'
                : 'O cadastro desta base está vazio.'
            }
            /* A seta é montada aqui, e não pela tabela: `@evogard/ui` serve as
               duas aplicações e não conhece o roteador de nenhuma — um `<a href>`
               lá dentro recarregaria a página inteira numa SPA. */
            rowLink={(v) => (
              <Link
                to="/erp/veiculos/$code"
                params={{ code: v.code }}
                aria-label={`Abrir ${v.placa ?? `o veículo ${v.code}`}`}
                className={rowLinkClasses}
              >
                <Icon nome="seta-direita" size={16} />
              </Link>
            )}
            columns={[
              {
                title: 'Placa',
                minWidth: '7rem',
                cell: (v) => v.placa ?? <NotProvided>sem placa</NotProvided>,
              },
              {
                title: 'Veículo',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (v) => (
                  <span>
                    {[v.marca, v.modelo].filter(Boolean).join(' ') || (
                      <NotProvided>não informado</NotProvided>
                    )}
                    {v.anoModelo && (
                      <span className="text-texto-suave"> · {v.anoModelo}</span>
                    )}
                  </span>
                ),
              },
              {
                title: 'Dono',
                minWidth: '14rem',
                cell: (v) => v.owner?.nome ?? <NotProvided>sem associado</NotProvided>,
              },
              {
                title: 'Situação',
                minWidth: '11rem',
                cell: (v) => <StatusBadge situacao={v.situacao} />,
              },
            ]}
          />

          {result?.estado === 'lida' && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="veículos"
              /* o cartão é `bleed`; o espaçamento é de quem entra nele */
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
